import {
  INTEGRATION_PROFILE_STATUS,
  INTEGRATION_TYPE,
  INTEGRATION_TYPE_DISPLAY,
  type IntegrationTypeId,
} from '@steerfi/shared';
import { IntegrationProfileConfigSchema } from '@steerfi/shared/types/jsonb/integration-profile.schemas';
import type { IntegrationProfileConfig } from '@steerfi/shared/types/jsonb/integration-profile.types';
import { IconPlugConnected } from '@tabler/icons-react';
import type React from 'react';
import { useState } from 'react';
import { createIntegrationProfile, IntegrationProfileApiError } from '@/api/integration-profiles';
import { ConfigJsonEditor } from '@/components/integration-profile/config-json-editor';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface AddIntegrationProfileModalProps {
  partyId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const STATUS_OPTIONS = [
  { value: INTEGRATION_PROFILE_STATUS.ACTIVE, label: 'Active' },
  { value: INTEGRATION_PROFILE_STATUS.INACTIVE, label: 'Inactive' },
  { value: INTEGRATION_PROFILE_STATUS.PENDING, label: 'Pending' },
] as const;

const TYPE_OPTIONS = (Object.entries(INTEGRATION_TYPE) as [string, number][]).map(([, id]) => ({
  value: id,
  label: INTEGRATION_TYPE_DISPLAY[id as IntegrationTypeId] ?? String(id),
}));

const sanitizeConfigForSubmit = (
  config: IntegrationProfileConfig | null
): IntegrationProfileConfig | null => {
  if (!config) return config;
  if (config.acquisition?.sftp?.enabled === false) {
    const { acquisition: _, ...rest } = config;
    return rest as IntegrationProfileConfig;
  }
  return config;
};

const isValidUrl = (url: string): boolean => {
  try {
    const { protocol } = new URL(url);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
};
const isValidTz = (tz: string) => tz === 'UTC' || Intl.supportedValuesOf('timeZone').includes(tz);
const isValidCron = (expr: string): boolean => {
  const parts = expr.trim().split(/\s+/);
  return parts.length === 5 && parts.every((p) => /^[\d,\-/*]+$/.test(p));
};
const friendlyConfigError = (
  issue: { path: (string | number)[]; message: string } | undefined
): string => {
  if (!issue) return 'Invalid configuration';
  const p = issue.path;
  if (p[0] === 'delivery' && p[1] === 'sftp' && p[2] === 'directories')
    return 'At least one delivery directory is required';
  if (p[0] === 'delivery' && p[1] === 'sftp' && p[2] === 'secretArn')
    return 'Delivery secret reference is required';
  if (p[0] === 'acquisition' && p[1] === 'sftp' && p[2] === 'secretArn')
    return 'Acquisition secret reference is required';
  if (p[0] === 'acquisition' && p[1] === 'sftp' && p[2] === 'rules' && p.length === 3)
    return 'At least one acquisition rule is required';
  if (p[0] === 'acquisition' && p[1] === 'sftp' && p[2] === 'rules' && p[4] === 'fileFormatId')
    return `File format is required for acquisition rule ${Number(p[3]) + 1}`;
  const path = p.length ? p.join('.') : null;
  return path ? `${path}: ${issue.message}` : issue.message;
};

export const AddIntegrationProfileModal: React.FC<AddIntegrationProfileModalProps> = ({
  partyId,
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [profile_name, setProfileName] = useState('');
  const [integration_type_id, setIntegrationTypeId] = useState<number>(INTEGRATION_TYPE.SFTP);
  const [environment, setEnvironment] = useState('');
  const [status_id, setStatusId] = useState<number>(INTEGRATION_PROFILE_STATUS.ACTIVE);
  const [config_json, setConfigJson] = useState<IntegrationProfileConfig | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<string, string>>>({});
  const [rootError, setRootError] = useState<string | null>(null);
  const [hasConfigError, setHasConfigError] = useState(false);

  const resetForm = () => {
    setProfileName('');
    setIntegrationTypeId(INTEGRATION_TYPE.SFTP);
    setEnvironment('');
    setStatusId(INTEGRATION_PROFILE_STATUS.ACTIVE);
    setConfigJson(null);
    setFieldErrors({});
    setRootError(null);
    setHasConfigError(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetForm();
    onOpenChange(next);
  };

  const validate = (): boolean => {
    const errs: Partial<Record<string, string>> = {};
    if (!profile_name.trim()) errs.profile_name = 'Name is required';
    if (!environment.trim()) errs.environment = 'Environment is required';
    if (integration_type_id === INTEGRATION_TYPE.SFTP) {
      const deliverySftp = config_json?.delivery?.sftp;
      if (!deliverySftp?.secretArn) {
        errs.config_json = 'Delivery secret reference is required';
      } else if (!deliverySftp.directories?.length) {
        errs.config_json = 'At least one delivery directory is required';
      }
    }
    if (integration_type_id === INTEGRATION_TYPE.API) {
      const apiBaseUrl = config_json?.delivery?.api?.baseUrl;
      if (!apiBaseUrl || !isValidUrl(apiBaseUrl)) {
        errs.config_json = 'A valid base URL is required for API delivery';
      }
    }
    if (!errs.config_json && config_json !== null) {
      const result = IntegrationProfileConfigSchema.safeParse(sanitizeConfigForSubmit(config_json));
      if (!result.success) {
        errs.config_json = friendlyConfigError(result.error.errors[0]);
      }
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRootError(null);
    if (!validate()) return;
    try {
      setSubmitting(true);
      await createIntegrationProfile(partyId, {
        profile_name: profile_name.trim(),
        integration_type_id,
        environment: environment.trim(),
        status_id,
        config_json: sanitizeConfigForSubmit(config_json),
      });
      resetForm();
      onSuccess();
      onOpenChange(false);
    } catch (e: unknown) {
      if (e instanceof IntegrationProfileApiError && e.status === 409) {
        const msg =
          (typeof e.body?.message === 'string' && e.body.message) ||
          'An integration profile with that name already exists for this provider, type, and environment';
        setFieldErrors((prev) => ({ ...prev, profile_name: msg }));
      } else {
        setRootError(e instanceof Error ? e.message : 'Failed to create integration profile');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const apiBaseUrl = config_json?.delivery?.api?.baseUrl;
  const hasBadBaseUrl =
    integration_type_id === INTEGRATION_TYPE.API && (!apiBaseUrl || !isValidUrl(apiBaseUrl));

  const sftpAcq = config_json?.acquisition?.sftp;
  const hasBadTimezone =
    (!!sftpAcq?.timezone && !isValidTz(sftpAcq.timezone)) ||
    (sftpAcq?.rules?.some((r) => !!r.timezone && !isValidTz(r.timezone)) ?? false);
  const hasBadCron =
    (!!sftpAcq?.scheduleCron && !isValidCron(sftpAcq.scheduleCron)) ||
    (sftpAcq?.rules?.some((r) => !!r.scheduleCron && !isValidCron(r.scheduleCron)) ?? false);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[620px] max-h-[85vh] !flex !flex-col overflow-hidden p-0 gap-0">
        <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
          <DialogHeader className="shrink-0 border-b bg-background px-6 py-4">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <IconPlugConnected className="h-5 w-5 text-muted-foreground" />
              Add Integration Profile
            </DialogTitle>
            <DialogDescription>
              Define how the platform talks to this provider for delivering and acquiring files.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Profile</p>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_10rem]">
              <div className="space-y-2">
                <Label htmlFor="add-ip-name">
                  Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="add-ip-name"
                  autoFocus
                  placeholder="e.g. Production SFTP"
                  maxLength={100}
                  value={profile_name}
                  onChange={(e) => setProfileName(e.target.value)}
                  aria-invalid={!!fieldErrors.profile_name}
                />
                {fieldErrors.profile_name && (
                  <p className="text-sm text-destructive">{fieldErrors.profile_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>
                  Type <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={String(integration_type_id)}
                  onValueChange={(v) => {
                    setIntegrationTypeId(Number(v));
                    setConfigJson(null);
                    setFieldErrors((prev) => {
                      const { config_json: _, ...rest } = prev;
                      return rest;
                    });
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={String(opt.value)}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_10rem]">
              <div className="space-y-2">
                <Label htmlFor="add-ip-env">
                  Environment <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="add-ip-env"
                  placeholder="e.g. production"
                  value={environment}
                  onChange={(e) => setEnvironment(e.target.value)}
                  aria-invalid={!!fieldErrors.environment}
                />
                {fieldErrors.environment && (
                  <p className="text-sm text-destructive">{fieldErrors.environment}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={String(status_id)} onValueChange={(v) => setStatusId(Number(v))}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={String(opt.value)}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
              Configuration <span className="text-destructive normal-case">*</span>
            </p>
            {fieldErrors.config_json && (
              <p className="text-sm text-destructive">{fieldErrors.config_json}</p>
            )}

            <ConfigJsonEditor
              integrationTypeId={integration_type_id}
              value={config_json}
              onChange={setConfigJson}
              onConfigError={setHasConfigError}
              providerPartyId={partyId}
              mode="create"
            />
          </div>

          <div className="shrink-0 border-t bg-background px-6 py-4 space-y-3">
            {rootError && <p className="text-sm text-destructive">{rootError}</p>}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="rounded-lg"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="rounded-lg"
                disabled={
                  submitting || hasBadBaseUrl || hasBadTimezone || hasBadCron || hasConfigError
                }
              >
                {submitting ? 'Creating…' : 'Create'}
              </Button>
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
